import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  FileText, 
  Database, 
  Calculator, 
  CheckCircle, 
  Upload, 
  Link, 
  Calendar,
  User,
  Download,
  Trash2
} from 'lucide-react';

const Evidence = ({ onSubmit, onNext, onPrevious, initialData = {} }) => {
  const { t } = useTranslation();
  const [evidenceItems, setEvidenceItems] = useState(initialData.evidenceItems || []);
  const [selectedSource, setSelectedSource] = useState('documents');
  const [newEvidence, setNewEvidence] = useState({
    name: '',
    source: '',
    type: 'document',
    attachedBy: '',
    timestamp: new Date().toISOString().split('T')[0]
  });

  const evidenceSources = [
    { id: 'documents', label: t('workflow.auditQA.evidence.sources.documents'), icon: FileText },
    { id: 'dataLog', label: t('workflow.auditQA.evidence.sources.dataLog'), icon: Database },
    { id: 'calculations', label: t('workflow.auditQA.evidence.sources.calculations'), icon: Calculator },
    { id: 'assumptions', label: t('workflow.auditQA.evidence.sources.assumptions'), icon: CheckCircle }
  ];

  const handleAddEvidence = () => {
    if (newEvidence.name && newEvidence.source) {
      const evidence = {
        ...newEvidence,
        id: Date.now(),
        type: selectedSource,
        attachedBy: 'Current User', // This would come from auth context
        timestamp: new Date().toISOString()
      };
      
      setEvidenceItems([...evidenceItems, evidence]);
      setNewEvidence({
        name: '',
        source: '',
        type: 'document',
        attachedBy: '',
        timestamp: new Date().toISOString().split('T')[0]
      });
    }
  };

  const handleRemoveEvidence = (id) => {
    setEvidenceItems(evidenceItems.filter(item => item.id !== id));
  };


  const getSourceIcon = (sourceType) => {
    const source = evidenceSources.find(s => s.id === sourceType);
    return source ? source.icon : FileText;
  };

  const getSourceLabel = (sourceType) => {
    const source = evidenceSources.find(s => s.id === sourceType);
    return source ? source.label : sourceType;
  };

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {t('workflow.auditQA.evidence.title')}
        </h2>
        
        <p className="text-gray-600 mb-8">
          {t('workflow.auditQA.evidence.description')}
        </p>

        {/* Quick Attach Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('workflow.auditQA.evidence.quickAttach')}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {evidenceSources.map((source) => {
              const IconComponent = source.icon;
              return (
                <button
                  key={source.id}
                  onClick={() => setSelectedSource(source.id)}
                  className={`p-4 rounded-lg border-2 transition-all duration-200 ${
                    selectedSource === source.id
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  <IconComponent className="w-6 h-6 mx-auto mb-2" />
                  <span className="text-sm font-medium">{source.label}</span>
                </button>
              );
            })}
          </div>

          {/* Add Evidence Form */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.auditQA.evidence.name')}
                </label>
                <input
                  type="text"
                  value={newEvidence.name}
                  onChange={(e) => setNewEvidence({...newEvidence, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.auditQA.evidence.namePlaceholder')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('workflow.auditQA.evidence.source')}
                </label>
                <input
                  type="text"
                  value={newEvidence.source}
                  onChange={(e) => setNewEvidence({...newEvidence, source: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={t('workflow.auditQA.evidence.sourcePlaceholder')}
                />
              </div>
              
              <div className="flex items-end">
                <button
                  type="button"
                  onClick={handleAddEvidence}
                  className="w-full bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors flex items-center justify-center"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {t('workflow.auditQA.evidence.attach')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Evidence List */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            {t('workflow.auditQA.evidence.attachedEvidence')} ({evidenceItems.length})
          </h3>
          
          {evidenceItems.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>{t('workflow.auditQA.evidence.noEvidence')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {evidenceItems.map((item) => {
                const IconComponent = getSourceIcon(item.type);
                return (
                  <div key={item.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <div className="flex-shrink-0">
                          <IconComponent className="w-8 h-8 text-blue-600" />
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h4 className="text-sm font-medium text-gray-900 truncate">
                              {item.name}
                            </h4>
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {getSourceLabel(item.type)}
                            </span>
                          </div>
                          
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <div className="flex items-center">
                              <Link className="w-3 h-3 mr-1" />
                              {item.source}
                            </div>
                            <div className="flex items-center">
                              <User className="w-3 h-3 mr-1" />
                              {item.attachedBy}
                            </div>
                            <div className="flex items-center">
                              <Calendar className="w-3 h-3 mr-1" />
                              {new Date(item.timestamp).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          type="button"
                          className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                          title={t('workflow.auditQA.evidence.download')}
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveEvidence(item.id)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                          title={t('workflow.auditQA.evidence.remove')}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end pt-6 border-t border-gray-200">
          <button
            type="button"
            className="px-6 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
          >
            {t('workflow.auditQA.evidence.generateBundle')}
          </button>
        </div>
    </div>
  );
};

export default Evidence;
